/** Display-only labels for Task.status. Stored values stay pending/assigned/approve/cancel. */
const TASK_STATUS_LABELS = {
  pending: 'Pending',
  assigned: 'Assigned',
  approve: 'Completed',
  approved: 'Completed',
  complete: 'Completed',
  completed: 'Completed',
  cancel: 'Cancelled',
  cancelled: 'Cancelled',
};

export function normalizeTaskStatusKey(status) {
  return String(status || 'pending')
    .toLowerCase()
    .trim()
    .replace(/_/g, ' ')
    .replace(/\s+/g, '');
}

export function mapTaskStatusToDisplay(status) {
  const key = normalizeTaskStatusKey(status);
  return TASK_STATUS_LABELS[key] || (status ? String(status) : 'Pending');
}

/** CSS suffix for Admin task badges. Maps approve → completed without renaming the enum. */
export function taskStatusBadgeClass(status) {
  const key = normalizeTaskStatusKey(status);
  if (key === 'approve' || key === 'approved' || key === 'complete' || key === 'completed') {
    return 'completed';
  }
  if (key === 'cancel' || key === 'cancelled') return 'cancelled';
  if (key === 'assigned') return 'assigned';
  return key || 'pending';
}

export function formatPersonName(user) {
  if (!user) return '';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || user.username || '';
}

export function uniqueIds(ids) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = String(raw || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Primary user first, then other assignees. Used so save sends the full intended set. */
export function collectTaskPeople(task) {
  const seen = new Set();
  const people = [];
  const add = (user) => {
    const id = String(user?.id || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    people.push(user);
  };
  add(task?.user);
  (Array.isArray(task?.assignees) ? task.assignees : []).forEach(add);
  return people;
}

export function collectTaskAssigneeIds(task) {
  return collectTaskPeople(task)
    .map((user) => String(user.id || '').trim())
    .filter(Boolean);
}

export function taskAssigneesWereLoaded(task) {
  return Array.isArray(task?.assignees);
}

export function formatTaskAssigneeNames(task) {
  const names = collectTaskPeople(task)
    .map(formatPersonName)
    .filter(Boolean);
  return names.length ? names.join(', ') : '—';
}

export function taskAssignmentChanged(previousIds, nextIds) {
  const previous = uniqueIds(previousIds);
  const next = uniqueIds(nextIds);
  if (previous.length !== next.length) return true;
  const previousSet = new Set(previous);
  return next.some((id) => !previousSet.has(id));
}

/**
 * Roster writes the same task_assignees relation. applyAssignees() replaces the
 * whole set when assigneeIds is sent.
 *
 * - Create: send assigneeIds only when someone is selected.
 * - Update: omit assignment fields when the set did not change, so a title/date
 *   save cannot wipe rostered workers.
 * - Update with a real assignment change: send the full loaded assigneeIds list.
 *   If assignees were not loaded, send userId only so the backend merges.
 */
export function applyTaskAssignmentToInput(input, selectedIds, options = {}) {
  const ids = uniqueIds(selectedIds);
  const isUpdate = Boolean(options.isUpdate);
  const assigneesLoaded = options.assigneesLoaded !== false;

  if (isUpdate) {
    if (!taskAssignmentChanged(options.previousIds, ids)) {
      return input;
    }
    if (assigneesLoaded) {
      input.assigneeIds = ids;
      input.userId = ids[0] || null;
    } else if (ids[0]) {
      input.userId = ids[0];
    }
    return input;
  }

  if (ids.length) {
    input.assigneeIds = ids;
    input.userId = ids[0];
  }
  return input;
}

const TASK_META = /Service:\s*[0-9a-f-]{36}|Service:\s*[^\n]*?(?=\s+[A-Z]|$)|(?:Requirement|Role|Frequency):\s*[A-Za-z_]+/gi;

/**
 * Quote/project tasks pack catalog metadata into the description
 * ("Service:<uuid> Service: <name> … Requirement: X Role: Y Frequency: Z").
 * Returns the human text and a readable role, like the manager portal shows.
 */
export function cleanTaskDescription(raw, title = '') {
  const src = String(raw || '').trim();
  const role = (src.match(/Role:\s*([A-Za-z_]+)/i) || [])[1] || '';
  let description = src.replace(TASK_META, ' ').replace(/\s+/g, ' ').trim();
  const t = String(title || '').trim();
  if (t && description.toLowerCase().startsWith(t.toLowerCase())) {
    description = description.slice(t.length).replace(/^[-—:,.\s]+/, '').trim();
  }
  const roleLabel = role.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return { description, roleLabel };
}
